<script setup lang="ts">
    import { onMounted, onUnmounted, ref, watch } from 'vue';
    import debounce from 'lodash/debounce';
    import {
        getReleaseSearchLibraries,
        getReleaseSearchScopes,
        searchReleases,
        type ReleaseSearchLibrary,
        type ReleaseSearchResult
    } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper';
    import { formatNumber } from '@/formatters.ts';
    import ReleaseFilterPanel from './ReleaseFilterPanel.vue';
    import ReleaseSearchTable from './ReleaseSearchTable.vue';
    import { useReleaseSearchFilters, type ReleaseSearchFilterState } from './useReleaseSearchFilters';

    const { filters, apiFilter, activeChips, clearAll } = useReleaseSearchFilters();

    function applyFilterPatch(patch: Partial<ReleaseSearchFilterState>): void {
        Object.assign(filters, patch);
    }

    const releases = ref<ReleaseSearchResult[]>([]);
    const loading = ref(true);
    const isTruncated = ref(false);
    const appliedTake = ref(0);

    const libraryOptions = ref<ReleaseSearchLibrary[]>([]);
    const scopeOptions = ref<string[]>([]);
    const lookupsLoading = ref(true);

    // Filters can change faster than the server answers, so each response is
    // checked against the newest request before it is allowed to win.
    let latestRequestId = 0;

    async function loadReleases(): Promise<void> {
        const requestId = ++latestRequestId;
        loading.value = true;
        try {
            const results = await searchReleases(http, apiFilter.value);
            if (requestId !== latestRequestId) return;
            releases.value = results.releases;
            isTruncated.value = results.isTruncated;
            appliedTake.value = results.take;
        } finally {
            if (requestId === latestRequestId) loading.value = false;
        }
    }

    // Typing in the free-text boxes changes the filter on every keystroke, so
    // the query is debounced rather than fired per character.
    const loadReleasesDebounced = debounce(() => void loadReleases(), 300);

    onUnmounted(() => loadReleasesDebounced.cancel());

    watch(apiFilter, loadReleasesDebounced, { deep: true });

    async function loadLookups(): Promise<void> {
        lookupsLoading.value = true;
        try {
            const [libraries, scopes] = await Promise.all([
                getReleaseSearchLibraries(http),
                getReleaseSearchScopes(http)
            ]);
            libraryOptions.value = libraries;
            scopeOptions.value = scopes;
        } finally {
            lookupsLoading.value = false;
        }
    }

    onMounted(async () => {
        await Promise.all([loadReleases(), loadLookups()]);
    });
</script>

<template>
    <div>
        <div class="columns">
            <div class="column">
                <h1 class="title is-3">
                    <b-icon icon="crane" />
                    Releases
                </h1>
            </div>
            <div class="column is-clearfix">
                <div class="field is-grouped is-pulled-right">
                    <p class="control">
                        <a
                            href="/Log"
                            class="button is-info">
                            <b-icon
                                icon="format-list-numbered"
                                size="is-small" />
                            <span>Log</span>
                        </a>
                    </p>
                </div>
            </div>
        </div>

        <div class="search-layout">
            <release-filter-panel
                :filters="filters"
                :library-options="libraryOptions"
                :scope-options="scopeOptions"
                :lookups-loading="lookupsLoading"
                @update="applyFilterPatch"
                @clear="clearAll" />

            <div class="search-results">
                <div
                    v-if="activeChips.length > 0"
                    class="filter-chips mb-2">
                    <button
                        v-for="chip in activeChips"
                        :key="chip.key"
                        class="filter-chip"
                        type="button"
                        :aria-label="`Remove filter ${chip.label}`"
                        @click="chip.remove()">
                        <span>{{ chip.label }}</span>
                        <b-icon
                            icon="close"
                            size="is-small" />
                    </button>
                </div>

                <div class="is-flex is-align-items-center is-justify-content-space-between">
                    <p class="is-size-6 has-text-grey">
                        {{ formatNumber(releases.length) }} {{ releases.length === 1 ? 'release' : 'releases' }}
                    </p>
                </div>

                <release-search-table
                    :releases="releases"
                    :loading="loading" />

                <b-message
                    v-if="isTruncated"
                    type="is-info"
                    :closable="false"
                    class="mt-2 mb-0">
                    Showing the newest {{ formatNumber(appliedTake) }} releases only. Narrow the filters to see older ones.
                </b-message>
            </div>
        </div>
    </div>
</template>

<style scoped>
  .search-layout {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
  }

  .search-results {
    flex: 1;
    min-width: 0;
  }

  .filter-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  /* The whole chip removes its filter, so the icon is a hint rather than a
     separate hit target. */
  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    border: 0;
    border-radius: 1rem;
    padding: 0.2rem 0.5rem 0.2rem 0.75rem;
    background-color: var(--eq-norwegian-woods-1);
    color: var(--eq-white);
    font-size: 0.875rem;
    line-height: 1.4;
    cursor: pointer;
  }

  .filter-chip:hover,
  .filter-chip:focus-visible {
    background-color: var(--eq-additional-1);
  }
</style>
